import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import type { SubmissionWithDocuments } from '@/types'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  FileText,
  MoreVertical,
  Eye,
  Archive,
  Trash2
} from 'lucide-react'

interface SubmissionCardProps {
  submission: SubmissionWithDocuments
  onArchive?: (id: string) => void
  onDelete?: (id: string) => void
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  processing: { label: 'Processing', variant: 'default' },
  review: { label: 'In Review', variant: 'outline' },
  complete: { label: 'Complete', variant: 'default' },
  archived: { label: 'Archived', variant: 'secondary' }
}

export function SubmissionCard({ submission, onArchive, onDelete }: SubmissionCardProps) {
  const status = statusConfig[submission.status] || statusConfig.draft
  const documentCount = submission.documents?.length || 0

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <Link
            to={`/workspace/${submission.id}`}
            className="font-semibold hover:underline"
          >
            {submission.insured_name}
          </Link>
          <p className="text-xs text-muted-foreground">
            Created {format(new Date(submission.created_at), 'MMM d, yyyy')}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/workspace/${submission.id}`}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </Link>
            </DropdownMenuItem>
            {submission.status !== 'archived' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onArchive?.(submission.id)}>
                  <Archive className="mr-2 h-4 w-4" />
                  Archive
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete?.(submission.id)}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Badge variant={status.variant}>{status.label}</Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              {documentCount} {documentCount === 1 ? 'document' : 'documents'}
            </div>
          </div>
          {submission.risk_score !== null && (
            <div
              className={cn(
                'text-sm font-medium',
                submission.risk_score >= 80
                  ? 'text-green-600'
                  : submission.risk_score >= 60
                  ? 'text-yellow-600'
                  : 'text-red-600'
              )}
            >
              Risk: {submission.risk_score}%
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
